import { useState } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo, faqs, reviews } from '@/mocks/comics';

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9otihaftmtnfcfh0dtg', {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Header />
      <main className="min-h-screen">
        <div className="pt-24 pb-10 max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
          </div>

          <section className="mb-14">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-4">
              关于{siteInfo.name}
            </h1>
            <p className="text-sm md:text-base text-foreground-500 text-center max-w-3xl mx-auto mb-10 leading-relaxed">
              {siteInfo.description}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {siteInfo.features.map((feature) => (
                <div key={feature.title} className="p-6 rounded-xl bg-background-100 hover:bg-background-200/70 transition-colors">
                  <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center mb-4">
                    <i className={`${feature.icon} text-xl text-accent-600`}></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-950 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-14">
            <div className="bg-gradient-to-r from-primary-100/80 to-accent-100/80 rounded-2xl p-8 md:p-12">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-3xl md:text-4xl font-heading font-bold text-primary-600 mb-1">
                    {siteInfo.totalComics.toLocaleString()}+
                  </div>
                  <div className="text-sm text-foreground-600">漫画作品</div>
                </div>
                <div>
                  <div className="text-3xl md:text-4xl font-heading font-bold text-accent-600 mb-1">
                    {Math.floor(siteInfo.totalUsers / 10000)}万+
                  </div>
                  <div className="text-sm text-foreground-600">注册用户</div>
                </div>
                <div>
                  <div className="text-3xl md:text-4xl font-heading font-bold text-primary-600 mb-1">
                    {Math.floor(siteInfo.totalViews / 100000000)}亿+
                  </div>
                  <div className="text-sm text-foreground-600">累计阅读量</div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-8">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {reviews.map((review, idx) => (
                <div key={idx} className="p-5 rounded-xl bg-background-100">
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={review.avatar}
                      alt={review.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{review.name}</div>
                      <div className="text-xs text-foreground-500">{review.date}</div>
                    </div>
                  </div>
                  <div className="flex gap-0.5 mb-2">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <div key={i} className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500 text-xs"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-8">
              常见问题
            </h2>
            <div className="max-w-3xl mx-auto divide-y divide-background-200">
              {faqs.map((faq, idx) => (
                <div key={idx} className="py-4">
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between text-left cursor-pointer"
                  >
                    <h3 className="text-sm md:text-base font-medium text-foreground-900 pr-4">
                      {faq.question}
                    </h3>
                    <div className={`w-5 h-5 flex-shrink-0 flex items-center justify-center transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-foreground-500"></i>
                    </div>
                  </button>
                  {openFaq === idx && (
                    <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section className="max-w-2xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-8">
              联系我们
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <div className="p-5 rounded-xl bg-background-100 text-center">
                <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-mail-line text-accent-600 text-lg"></i>
                </div>
                <div className="text-xs text-foreground-500 mb-1">电子邮箱</div>
                <div className="text-sm font-medium text-foreground-800">{siteInfo.email}</div>
              </div>
              <div className="p-5 rounded-xl bg-background-100 text-center">
                <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-qq-line text-accent-600 text-lg"></i>
                </div>
                <div className="text-xs text-foreground-500 mb-1">QQ群</div>
                <div className="text-sm font-medium text-foreground-800">{siteInfo.qq}</div>
              </div>
              <div className="p-5 rounded-xl bg-background-100 text-center">
                <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-wechat-line text-accent-600 text-lg"></i>
                </div>
                <div className="text-xs text-foreground-500 mb-1">微信公众号</div>
                <div className="text-sm font-medium text-foreground-800">{siteInfo.wechat}</div>
              </div>
            </div>

            <div className="p-6 md:p-8 rounded-2xl bg-background-100">
              <h3 className="text-base font-heading font-semibold text-foreground-950 mb-1">发送消息</h3>
              <p className="text-xs text-foreground-500 mb-6">如有任何问题或建议，欢迎给我们留言</p>

              {formStatus === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className="ri-check-line text-2xl text-accent-600"></i>
                  </div>
                  <p className="text-foreground-800 font-medium">发送成功！</p>
                  <p className="text-sm text-foreground-500 mt-1">感谢您的留言，我们会尽快回复。</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form="true" id="contact-form">
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入主题"
                    />
                  </div>
                  <div className="mb-5">
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言（最多500字）"
                    />
                  </div>
                  {formStatus === 'error' && formError && (
                    <div className="mb-4 p-3 rounded-lg bg-primary-50 text-sm text-primary-700">
                      {formError}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {formStatus === 'submitting' ? '发送中...' : '发送留言'}
                  </button>
                </form>
              )}
            </div>
          </section>
        </div>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: `关于${siteInfo.name}`,
            description: siteInfo.description,
            url: 'https://hanmanonline.com/about',
            isPartOf: {
              '@type': 'WebSite',
              name: siteInfo.name,
              url: 'https://hanmanonline.com',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hanmanonline.com' },
                { '@type': 'ListItem', position: 2, name: '关于我们' },
              ],
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            })),
          }),
        }}
      />
    </>
  );
}